import { createPinia } from 'pinia';
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import './style.css';
import { isElectron, loadRuntimeConfig } from './config/runtime';
import { rewriteElectronHubFetchUrl } from './services/hubFetch';
import { createLocaleI18n } from './locales';
import { installDragSelectionGuard } from './utils/dragSelectionGuard';
import HeaderPortal from './components/HeaderPortal.vue';

type CryptoWithRandomUUID = { randomUUID?: () => string };

// Polyfill crypto.randomUUID() for Android WebView < Chrome 92. `randomUUID` is
// ES2021: the view below widens it to a plain `() => string` (the lib declares
// the template-literal return type) and admits it may be missing.
const cryptoApi: CryptoWithRandomUUID | undefined = typeof crypto === 'undefined' ? undefined : crypto;
if (cryptoApi && typeof cryptoApi.randomUUID !== 'function') {
  cryptoApi.randomUUID = function (): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  };
}
import { initializePWA } from './services/pwa';
import './services/axios-config'; // Initialize axios interceptors
import { brand } from './config/brand';
import { assetUrl } from './utils/assetUrl';
import { installBootTiming } from './utils/bootTiming';

// Before anything else: record the boot's three numbers (responseEnd,
// DOMContentLoaded, first hub answer) and log them, so a startup regression is a
// number in the log instead of an inference from request counts (t_3b15c05b).
installBootTiming();

// brand.images.* hold absolute web paths ('/images/OttoHead.png'). Under a
// relative base (Electron/embedded builds loading from file://) an absolute
// path resolves to the filesystem root and the image is blank in release
// builds while dev works. Normalize once here so every consumer — templates,
// inline styles, script-setup constants — resolves through the app base.
for (const [key, value] of Object.entries(brand.images)) {
  (brand.images as unknown as Record<string, string>)[key] = assetUrl(value);
}

installDragSelectionGuard();

/**
 * Every `fetch` this page makes to a hub goes through `otto-local://`, the protocol the main process
 * proxies to the hub (CORS, self-signed certs, the transport ladder).
 *
 * The rewrite goes through `rewriteElectronHubFetchUrl` (`services/hubFetch.ts`) rather than
 * `rewriteElectronLocalRequestUrl` directly, and the difference is the hub's **port**: Chromium's URL
 * parser drops the port of an `otto-local://` authority (`new URL('otto-local://h:16162/x').port` is
 * `''`), so the request would arrive in the main process as `otto-local://h/x` and be answered by
 * whichever hub on that host answered last — a silent misroute, measured on card `t_e4c55b5f` (a
 * fetch naming the hub on :16162 was answered by the hub on :16161). The helper moves the port into a
 * path segment Chromium keeps; `electron/localProtocolLadder.ts` reads it back and honours it.
 */
function installElectronLocalFetchBridge(): void {
  if (!isElectron() || typeof window === 'undefined' || window.__ottoElectronFetchBridgeInstalled) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  const rewriteInput = (input: RequestInfo | URL): RequestInfo | URL => {
    if (typeof input === 'string') {
      return rewriteElectronHubFetchUrl(input);
    }

    if (input instanceof URL) {
      const rewritten = rewriteElectronHubFetchUrl(input.toString());
      return rewritten === input.toString() ? input : new URL(rewritten);
    }

    if (typeof Request !== 'undefined' && input instanceof Request) {
      const rewritten = rewriteElectronHubFetchUrl(input.url);
      return rewritten === input.url ? input : new Request(rewritten, input);
    }

    return input;
  };

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    return originalFetch(rewriteInput(input), init);
  }) as typeof window.fetch;

  window.__ottoElectronFetchBridgeInstalled = true;
}

// The bridge flag lives on `window` (not in a module variable) because the app
// bundle can be evaluated twice in the embedded shells; the global declaration
// is here because main.ts is the only writer and reader.
declare global {
  interface Window {
    __ottoElectronFetchBridgeInstalled?: boolean;
  }
}

// Load runtime configuration before mounting app
loadRuntimeConfig()
  .then(async (config) => {
    installElectronLocalFetchBridge();
    console.log('[Torik] Runtime config loaded:', config.mode);

    const app = createApp(App);

    // Enable Vue devtools during development or when explicitly enabled for production
    // Set VITE_ENABLE_VUE_DEVTOOLS=true to enable production devtools
    const devtoolsEnabled = import.meta.env.DEV || import.meta.env.VITE_ENABLE_VUE_DEVTOOLS === 'true';
    // `app.config.devtools` is the pre-3.4 runtime knob; Vue's public `AppConfig`
    // type has dropped it (@deprecated → the `__VUE_PROD_DEVTOOLS__` build flag).
    // Assigned through Object.assign so the runtime behaviour is unchanged.
    Object.assign(app.config, { devtools: devtoolsEnabled });
    console.log(
      '[Torik] Vue Devtools enabled:',
      devtoolsEnabled,
      '(DEV:',
      import.meta.env.DEV,
      ', VITE_ENABLE_VUE_DEVTOOLS:',
      import.meta.env.VITE_ENABLE_VUE_DEVTOOLS,
      ')',
    );

    app.use(createPinia());
    app.use(createLocaleI18n());
    app.component('HeaderPortal', HeaderPortal);
    app.use(router);

    await router.isReady();
    app.mount('#app');

    // Initialize PWA features
    await initializePWA();
  })
  .catch(async (error) => {
    console.error('[Torik] Failed to load runtime config:', error);
    // Mount app anyway with defaults
    const app = createApp(App);

    // Enable Vue devtools during development or when explicitly enabled for production
    // Set VITE_ENABLE_VUE_DEVTOOLS=true to enable production devtools
    const devtoolsEnabled = import.meta.env.DEV || import.meta.env.VITE_ENABLE_VUE_DEVTOOLS === 'true';
    // Same legacy runtime knob as above (see the comment there).
    Object.assign(app.config, { devtools: devtoolsEnabled });
    console.log(
      '[Torik] Vue Devtools enabled:',
      devtoolsEnabled,
      '(DEV:',
      import.meta.env.DEV,
      ', VITE_ENABLE_VUE_DEVTOOLS:',
      import.meta.env.VITE_ENABLE_VUE_DEVTOOLS,
      ')',
    );

    app.use(createPinia());
    app.use(createLocaleI18n());
    app.component('HeaderPortal', HeaderPortal);
    app.use(router);

    await router.isReady();
    app.mount('#app');

    // Initialize PWA features
    initializePWA().catch(console.error);
  });
